import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useAuth } from './AuthContext';
import { SELECTED_LOCAL_STORAGE_KEY } from '../config/constants';
import { User } from '../types/user';

export interface LocalOption {
  id: string;
  label: string;
  name?: string;
  location?: string;
  shopId?: number | string;
}

interface LocalContextValue {
  selectedLocalId: string | null;
  setSelectedLocalId: (localId: string | null) => void;
  availableLocals: LocalOption[];
  registerAvailableLocals: (locals: LocalOption[]) => void;
  clearLocals: () => void;
}

const LocalContext = createContext<LocalContextValue | undefined>(undefined);

const getLocalIdFromUser = (user: User | null): string | null => {
  if (!user) return null;
  const candidateValues = [
    (user as unknown as { localId?: string | number })?.localId,
    (user as unknown as { local_id?: string | number })?.local_id,
    (user as unknown as { id_local?: string | number })?.id_local,
    user.dataUser?.localId,
    user.dataUser?.id_local
  ];

  for (const candidate of candidateValues) {
    if (candidate === undefined || candidate === null) continue;
    const value = String(candidate).trim();
    if (value.length > 0) {
      return value;
    }
  }

  return null;
};

const buildUserLocalOption = (user: User | null): LocalOption | null => {
  const localId = getLocalIdFromUser(user);
  if (!localId) return null;
  const label =
    (user as unknown as { nombre_local?: string })?.nombre_local ||
    user?.dataUser?.nombre_local ||
    `Local ${localId}`;

  return {
    id: localId,
    label,
    name: label
  };
};

const mergeLocalOptions = (current: LocalOption[], next: LocalOption[]): LocalOption[] => {
  if (!next.length) return current;
  const map = new Map<string, LocalOption>();
  current.forEach((option) => {
    map.set(option.id, option);
  });

  next.forEach((option) => {
    if (!option.id) return;
    const id = String(option.id);
    const existing = map.get(id) || { id, label: `Local ${id}` };
    map.set(id, {
      ...existing,
      ...option,
      id
    });
  });

  return Array.from(map.values());
};

export const LocalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [availableLocals, setAvailableLocals] = useState<LocalOption[]>(() => {
    const userOption = buildUserLocalOption(user);
    return userOption ? [userOption] : [];
  });
  const [selectedLocalId, setSelectedLocalIdState] = useState<string | null>(() => {
    try {
      if (typeof window !== 'undefined') {
        const stored = localStorage.getItem(SELECTED_LOCAL_STORAGE_KEY);
        if (stored && stored.trim().length > 0) {
          return stored;
        }
      }
    } catch {
      // Ignored: localStorage not available
    }
    return buildUserLocalOption(user)?.id ?? null;
  });

  const persistLocalId = useCallback((localId: string | null) => {
    try {
      if (localId) {
        localStorage.setItem(SELECTED_LOCAL_STORAGE_KEY, localId);
      } else {
        localStorage.removeItem(SELECTED_LOCAL_STORAGE_KEY);
      }
      if (typeof window !== 'undefined') {
        if (localId) {
          (window as unknown as { localId?: string }).localId = localId;
        } else {
          delete (window as unknown as { localId?: string }).localId;
        }
      }
    } catch {
      // Ignore storage errors to avoid blocking UI
    }
  }, []);

  const updateSelectedLocalId = useCallback(
    (localId: string | null) => {
      setSelectedLocalIdState(localId);
      persistLocalId(localId);
    },
    [persistLocalId]
  );

  const registerAvailableLocals = useCallback((locals: LocalOption[]) => {
    setAvailableLocals((prev) => mergeLocalOptions(prev, locals));
  }, []);

  const clearLocals = useCallback(() => {
    setAvailableLocals([]);
    updateSelectedLocalId(null);
  }, [updateSelectedLocalId]);

  // Register user local when user data changes
  useEffect(() => {
    if (!user) {
      clearLocals();
      return;
    }
    const userOption = buildUserLocalOption(user);
    if (userOption) {
      setAvailableLocals((prev) => mergeLocalOptions(prev, [userOption]));
    }
  }, [user, clearLocals]);

  // Ensure selected local stays in sync with available options
  useEffect(() => {
    if (selectedLocalId) {
      const exists = availableLocals.some((option) => option.id === selectedLocalId);
      if (!exists) {
        setAvailableLocals((prev) =>
          mergeLocalOptions(prev, [
            {
              id: selectedLocalId,
              label: `Local ${selectedLocalId}`
            }
          ])
        );
      }
    } else if (availableLocals.length > 0) {
      updateSelectedLocalId(availableLocals[0].id);
    }
  }, [availableLocals, selectedLocalId, updateSelectedLocalId]);

  const value = useMemo<LocalContextValue>(
    () => ({
      selectedLocalId,
      setSelectedLocalId: updateSelectedLocalId,
      availableLocals,
      registerAvailableLocals,
      clearLocals
    }),
    [availableLocals, registerAvailableLocals, selectedLocalId, updateSelectedLocalId, clearLocals]
  );

  return <LocalContext.Provider value={value}>{children}</LocalContext.Provider>;
};

export const useLocalSelection = (): LocalContextValue => {
  const context = useContext(LocalContext);
  if (!context) {
    throw new Error('useLocalSelection debe usarse dentro de un LocalProvider');
  }
  return context;
};

